/*
  kidsuper-custom.js
  Dev-owned custom scripts for the KidSuper Horizon theme.
  Loaded from layout/theme.liquid with defer. Namespace globals under window.KidSuper.
*/
window.KidSuper = window.KidSuper || {};

/*
  Sticky add-to-cart size row (mobile).

  Horizon's sticky bar (sections/product-information.liquid, assets/sticky-add-to-cart.js)
  shows the selected variant as text only. This adds a horizontally scrollable row of size
  pills to the bar, with a slim scroll indicator underneath. The pills are puppets: each one
  clicks the matching radio in the page's real <variant-picker>, so Horizon's own variant
  update runs and the bar's price, image and button morph exactly as they do when the
  shopper taps the picker on the page.

  Only the "size" option is mirrored (products with colour + size keep colour as picked on
  the page). Products with a single default variant get no row.

  Products tagged `shoe-size` carry too many sizes for a pill row, so for them the same
  wrapper holds a native <select> instead (iOS opens its picker wheel). The section stamps
  data-size-picker="select" | "pills" on <sticky-add-to-cart> from the tag
  (sections/product-information.liquid); nothing about tags is derived here.

  Markup injected into the bar (pills):
    <kidsuper-size-row class="sticky-add-to-cart__sizes">
      <div class="sticky-add-to-cart__options"> <button class="sticky-add-to-cart__option">…
      <div class="sticky-add-to-cart__scrollbar"><i></i></div>
    </kidsuper-size-row>
  and (select):
    <kidsuper-size-row class="sticky-add-to-cart__sizes" data-mode="select">
      <select class="sticky-add-to-cart__select"> <option>…
    </kidsuper-size-row>
  Both carry a hidden <p class="sticky-add-to-cart__soldout"> shown when every size is gone.
  The wrapper uses a tag no server-rendered child of the bar has: Horizon's morph pairs
  children by tag name, so a <div> would be morphed into the price element on variant change.
  The bar's children are re-morphed from server HTML on every variant change, which drops
  any element that isn't in that HTML, so the wrapper is re-inserted when that happens (the
  MutationObserver runs before the next paint, so nothing flickers). Selected / sold-out
  state is read from the real picker's inputs, which Horizon keeps as the source of truth.
*/
(function () {
  const WRAP_CLASS = "sticky-add-to-cart__sizes";
  const ROW_CLASS = "sticky-add-to-cart__options";
  const PILL_CLASS = "sticky-add-to-cart__option";
  const SELECT_CLASS = "sticky-add-to-cart__select";
  const SCROLLBAR_CLASS = "sticky-add-to-cart__scrollbar";
  const SOLDOUT_CLASS = "sticky-add-to-cart__soldout";
  const SIZE_PATTERN = /^(size|sizes|taille|talla|größe|grösse)$/i;

  /**
   * Find the size fieldset in the page's variant picker for this product.
   * Falls back to the last fieldset when no option is literally named "size".
   * @param {HTMLElement} sticky
   * @returns {HTMLFieldSetElement | null}
   */
  function findSizeFieldset(sticky) {
    const productId = sticky.dataset.productId;
    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!picker) return null;
    const fieldsets = Array.from(picker.querySelectorAll("fieldset.variant-option"));
    if (!fieldsets.length) return null;
    const bySize = fieldsets.find((fs) => {
      const legend = fs.querySelector("legend");
      const name = fs.querySelector("input")?.dataset.optionName ?? legend?.textContent ?? "";
      return SIZE_PATTERN.test(name.trim());
    });
    return /** @type {HTMLFieldSetElement} */ (bySize ?? fieldsets[fieldsets.length - 1]);
  }

  /**
   * Select the size behind a pill or option. Not input.click(): that also focuses the
   * (visually hidden) radio up in the page's variant picker, and Safari scrolls a newly
   * focused control into view, which threw the page to the top on iOS. Horizon's
   * <variant-picker> only listens for a bubbling `change`, so check the radio and dispatch
   * that directly.
   * @param {HTMLInputElement} input
   */
  function pick(input) {
    if (input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * The bar's "Sold out" wording. Horizon renders the button with the localised string when
   * the current variant is unavailable; otherwise fall back to English.
   * @returns {string}
   */
  function soldOutLabel() {
    const sticky = document.querySelector("sticky-add-to-cart");
    const buttonLabel =
      sticky?.dataset.variantAvailable !== "true"
        ? sticky?.querySelector(".add-to-cart-text__content span")?.textContent?.trim()
        : "";
    return buttonLabel || "Sold out";
  }

  /**
   * @typedef {object} SizeUi
   * @property {"pills" | "select"} mode
   * @property {HTMLElement} wrap
   * @property {HTMLElement | null} row      pills mode only
   * @property {HTMLElement | null} track    pills mode only
   * @property {HTMLElement | null} thumb    pills mode only
   * @property {HTMLSelectElement | null} select  select mode only
   * @property {number} lastScroll
   */

  /**
   * Build the wrapper and its controls from the real picker's inputs: a scroller of pills with
   * a scroll indicator, or a native select for `shoe-size` products.
   * @param {HTMLFieldSetElement} fieldset
   * @param {"pills" | "select"} mode
   * @returns {SizeUi}
   */
  function build(fieldset, mode) {
    const wrap = document.createElement("kidsuper-size-row");
    wrap.className = WRAP_CLASS;
    wrap.dataset.mode = mode;
    const label = fieldset.querySelector("legend")?.textContent?.trim() || "Size";
    const inputs = fieldset.querySelectorAll('input[type="radio"]');

    // Shown instead of the controls when every size is sold out.
    const soldOut = document.createElement("p");
    soldOut.className = SOLDOUT_CLASS;
    soldOut.hidden = true;
    soldOut.textContent = soldOutLabel();

    /** @type {SizeUi} */
    const ui = { mode, wrap, row: null, track: null, thumb: null, select: null, lastScroll: 0 };

    if (mode === "select") {
      const select = document.createElement("select");
      select.className = SELECT_CLASS;
      select.setAttribute("aria-label", label);
      for (const input of inputs) {
        const option = document.createElement("option");
        option.value = input.value;
        option.dataset.value = input.value;
        option.textContent = input.value;
        select.appendChild(option);
      }
      select.addEventListener("change", () => {
        const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
          (candidate) => candidate.value === select.value
        );
        if (input) pick(input);
      });
      wrap.append(select, soldOut);
      ui.select = select;
      return ui;
    }

    const row = document.createElement("div");
    row.className = ROW_CLASS;
    row.setAttribute("role", "group");
    row.setAttribute("aria-label", label);

    for (const input of inputs) {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = PILL_CLASS;
      pill.dataset.value = input.value;
      pill.textContent = input.value;
      pill.addEventListener("click", () => pick(input));
      row.appendChild(pill);
    }

    const track = document.createElement("div");
    track.className = SCROLLBAR_CLASS;
    track.setAttribute("aria-hidden", "true");
    const thumb = document.createElement("i");
    track.appendChild(thumb);

    wrap.append(row, track, soldOut);
    ui.row = row;
    ui.track = track;
    ui.thumb = thumb;

    // Track the scroll position so it can be restored after the bar is re-morphed: a
    // re-inserted element comes back at scrollLeft 0.
    row.addEventListener(
      "scroll",
      () => {
        ui.lastScroll = row.scrollLeft;
        updateScrollbar(row, track, thumb);
      },
      { passive: true }
    );

    return ui;
  }

  /**
   * Size and position the indicator thumb from the row's scroll state. Hidden when the pills
   * all fit.
   * @param {HTMLElement} row
   * @param {HTMLElement} track
   * @param {HTMLElement} thumb
   */
  function updateScrollbar(row, track, thumb) {
    const overflow = row.scrollWidth - row.clientWidth;
    if (overflow <= 1) {
      track.hidden = true;
      return;
    }
    track.hidden = false;
    const ratio = row.clientWidth / row.scrollWidth;
    const progress = row.scrollLeft / overflow;
    thumb.style.width = `${Math.max(ratio * 100, 12)}%`;
    thumb.style.left = `${progress * (100 - Math.max(ratio * 100, 12))}%`;
  }

  /**
   * Copy selected / available state from the real inputs onto the pills.
   * @param {HTMLElement} row
   * @param {HTMLFieldSetElement} fieldset
   * @returns {HTMLElement | null | false} the selected pill, null if none, false if the row is stale
   */
  function syncRow(row, fieldset) {
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const pills = Array.from(row.querySelectorAll(`.${PILL_CLASS}`));
    if (inputs.length !== pills.length) return false;

    syncAllSoldOut(row.parentElement, inputs);

    let selected = null;
    inputs.forEach((input, i) => {
      const pill = /** @type {HTMLElement} */ (pills[i]);
      if (pill.dataset.value !== input.value) {
        pill.dataset.value = input.value;
        pill.textContent = input.value;
      }
      const checked = input.checked || input.dataset.currentChecked === "true";
      const available = input.dataset.optionAvailable !== "false";
      pill.setAttribute("aria-pressed", String(checked));
      pill.dataset.selected = String(checked);
      pill.dataset.available = String(available);
      // ADA note 07: sold-out state was colour and line-through only. Reuse the main picker's own
      // (translated) label, e.g. "M - Sold out", so screen readers hear it too.
      if (available) pill.removeAttribute("aria-label");
      else pill.setAttribute("aria-label", input.getAttribute("aria-label") || input.value);
      if (checked) selected = pill;
    });
    return selected;
  }

  /**
   * Flag the wrapper when every size is sold out, so the CSS swaps the controls for the label.
   * @param {HTMLElement | null} wrap
   * @param {HTMLInputElement[]} inputs
   */
  function syncAllSoldOut(wrap, inputs) {
    if (!wrap) return;
    const allSoldOut = inputs.every((input) => input.dataset.optionAvailable === "false");
    wrap.dataset.allSoldOut = String(allSoldOut);
    const label = wrap.querySelector(`.${SOLDOUT_CLASS}`);
    if (label) label.hidden = !allSoldOut;
  }

  /**
   * Copy selected / available state from the real inputs onto the select's options. Sold-out
   * sizes stay selectable with a "Sold out" suffix, like Horizon's own dropdown picker and
   * the pills, so the bar's sold-out button state keeps working unchanged.
   * @param {HTMLSelectElement} select
   * @param {HTMLFieldSetElement} fieldset
   * @returns {boolean} false if the select is stale (option count differs from the inputs)
   */
  function syncSelect(select, fieldset) {
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const options = Array.from(select.options);
    if (inputs.length !== options.length) return false;

    syncAllSoldOut(select.parentElement, inputs);

    const suffix = ` – ${soldOutLabel()}`;
    let value = null;
    inputs.forEach((input, i) => {
      const option = options[i];
      const available = input.dataset.optionAvailable !== "false";
      const text = available ? input.value : input.value + suffix;
      if (option.value !== input.value) {
        option.value = input.value;
        option.dataset.value = input.value;
      }
      if (option.textContent !== text) option.textContent = text;
      option.dataset.available = String(available);
      if (input.checked || input.dataset.currentChecked === "true") value = input.value;
    });
    if (value !== null && select.value !== value) select.value = value;
    return true;
  }

  /**
   * Scroll the row so the pill sits in the middle. Sets the row's own scrollLeft rather than
   * using scrollIntoView, which could also scroll the page and does nothing useful while the
   * bar is still translated off-screen.
   * @param {HTMLElement} row
   * @param {HTMLElement} pill
   * @param {ScrollBehavior} behavior
   */
  function centreInRow(row, pill, behavior) {
    const left = Math.max(0, pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2);
    if (Math.abs(row.scrollLeft - left) < 1) return;
    row.scrollTo({ left, behavior });
  }

  /**
   * @param {HTMLElement} sticky
   */
  function init(sticky) {
    const bar = sticky.querySelector('[ref="stickyBar"]');
    if (!bar) return;

    let fieldset = findSizeFieldset(sticky);
    if (!fieldset) return;

    /** @type {"pills" | "select"} */
    const mode = sticky.dataset.sizePicker === "select" ? "select" : "pills";
    let ui = build(fieldset, mode);
    /** @type {string | null} value of the pill last centred; null until the first pass */
    let centredValue = null;

    const place = () => {
      if (ui.wrap.isConnected) return;
      const anchor = bar.querySelector(".sticky-add-to-cart__info");
      if (anchor) anchor.insertAdjacentElement("afterend", ui.wrap);
      else bar.appendChild(ui.wrap);
      // Restore the scroll position lost on re-insert, before anything paints.
      if (ui.row) ui.row.scrollLeft = ui.lastScroll;
      sticky.dataset.hasOptions = "true";
    };

    // Animate only when the selection moved; otherwise snap, so a re-morph of the bar
    // (which re-inserts the row) never plays a visible scroll. Nothing to do for the select.
    const centreSelected = () => {
      if (!ui.row || !ui.track || !ui.thumb) return;
      const selected = /** @type {HTMLElement | null} */ (
        ui.row.querySelector(`.${PILL_CLASS}[data-selected="true"]`)
      );
      if (selected) {
        const value = selected.dataset.value ?? "";
        const behavior = centredValue !== null && value !== centredValue ? "smooth" : "instant";
        centreInRow(ui.row, selected, behavior);
        centredValue = value;
      }
      updateScrollbar(ui.row, ui.track, ui.thumb);
    };

    /** @returns {boolean} false when the controls no longer match the inputs */
    const sync = () =>
      ui.select ? syncSelect(ui.select, fieldset) : ui.row ? syncRow(ui.row, fieldset) !== false : false;

    const refresh = () => {
      // The picker itself is morphed on variant change; re-resolve it in case it was replaced.
      fieldset = findSizeFieldset(sticky) ?? fieldset;
      if (!sync()) {
        ui.wrap.remove();
        ui = build(fieldset, mode);
        place();
        sync();
      }
      centreSelected();
    };

    place();
    refresh();

    // Re-insert the wrapper if the bar's children are re-morphed.
    new MutationObserver(() => {
      place();
      refresh();
    }).observe(bar, { childList: true });

    // Re-centre the selected pill each time the bar slides up, so it is in view on arrival.
    new MutationObserver(() => {
      if (bar.dataset.stuck === "true") centreSelected();
    }).observe(bar, { attributes: true, attributeFilter: ["data-stuck"] });

    // Follow selection changes made on the page's own picker.
    const picker = fieldset.closest("variant-picker") ?? document;
    picker.addEventListener("change", refresh);
    new MutationObserver(refresh).observe(picker, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-current-checked", "data-option-available", "checked"],
      childList: true,
    });

    window.addEventListener(
      "resize",
      () => {
        if (ui.row && ui.track && ui.thumb) updateScrollbar(ui.row, ui.track, ui.thumb);
      },
      { passive: true }
    );
  }

  /*
    Post-add state. After a successful add from the bar (announced by the
    `kidsuper:sticky-added` event that the KidSuper edit in sticky-add-to-cart.js dispatches),
    the bar's lower area fades out and two half-width controls fade in: Back to collection
    (a link to the collection the shopper came from, see collectionBackLink below) and
    Check out. The state resets whenever the bar hides. Same injection rules as the size
    row: custom tag, re-inserted after morph.
  */
  const POST_ADD_CLASS = "sticky-add-to-cart__post-add";

  /**
   * Where "Back to collection" goes, in order of preference:
   * 1. the collection-nav block's own back link (blocks/collection-nav.liquid), which Liquid
   *    resolves from the collection-aware URL and whose label is a Customizer setting; the
   *    block's aria-label carries the collection title;
   * 2. the /collections/<handle>/products/... URL itself, when the block is not on the page;
   * 3. /collections/all.
   * Shared with the recommendations "View all" page (window.KidSuper.collectionBackLink).
   * @returns {{ href: string, label: string, title: string | null }}
   */
  function collectionBackLink() {
    const fallbackLabel = "Back to collection";
    const nav = /** @type {HTMLAnchorElement | null} */ (
      document.querySelector("a.collection-nav__link--back[href]")
    );
    if (nav) {
      const title = nav.closest("nav")?.getAttribute("aria-label")?.trim() || null;
      return { href: nav.getAttribute("href") || "", label: nav.textContent?.trim() || fallbackLabel, title };
    }

    const root = (/** @type {any} */ (window).Shopify?.routes?.root ?? "/").replace(/\/$/, "");
    const match = window.location.pathname.match(/^\/collections\/([^/]+)\/products\//);
    if (match) return { href: `${root}/collections/${match[1]}`, label: fallbackLabel, title: null };

    return { href: `${root}/collections/all`, label: fallbackLabel, title: null };
  }
  window.KidSuper.collectionBackLink = collectionBackLink;

  /**
   * @param {HTMLElement} sticky
   */
  function initPostAdd(sticky) {
    const bar = sticky.querySelector('[ref="stickyBar"]');
    if (!bar) return;

    const t = /** @type {any} */ (window).Theme?.translations ?? {};
    const block = document.createElement("kidsuper-post-add");
    block.className = POST_ADD_CLASS;

    const back = collectionBackLink();
    const cont = document.createElement("a");
    cont.className = `${POST_ADD_CLASS}-continue`;
    cont.href = back.href;
    cont.textContent = back.label;

    const checkout = document.createElement("button");
    checkout.type = "button";
    checkout.className = `${POST_ADD_CLASS}-checkout`;
    checkout.textContent = t.checkout || "Check out";

    block.append(cont, checkout);

    const setState = (on) => {
      sticky.dataset.postAdd = on ? "true" : "false";
      block.setAttribute("aria-hidden", on ? "false" : "true");
      // Keep the faded controls out of the tab order while hidden.
      cont.tabIndex = checkout.tabIndex = on ? 0 : -1;
    };

    const place = () => {
      if (!block.isConnected) bar.appendChild(block);
    };

    place();
    setState(false);

    new MutationObserver(place).observe(bar, { childList: true });

    sticky.addEventListener("kidsuper:sticky-added", () => setState(true));
    checkout.addEventListener("click", () => {
      const root = /** @type {any} */ (window).Shopify?.routes?.root ?? "/";
      window.location.assign(`${root.replace(/\/$/, "")}/checkout`);
    });

    // Bar hidden (scrolled back to the buy buttons, or page bottom): back to normal.
    new MutationObserver(() => {
      if (bar.dataset.stuck !== "true" && sticky.dataset.postAdd === "true") setState(false);
    }).observe(bar, { attributes: true, attributeFilter: ["data-stuck"] });
  }

  const boot = () => {
    document.querySelectorAll("sticky-add-to-cart").forEach((el) => {
      if (el.dataset.optionsInit) return;
      el.dataset.optionsInit = "true";
      init(/** @type {HTMLElement} */ (el));
      initPostAdd(/** @type {HTMLElement} */ (el));
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Section re-renders in the theme editor.
  document.addEventListener("shopify:section:load", boot);

  window.KidSuper.stickyOptions = { boot };
})();

/*
  Collection context for product cards loaded by fetch.

  snippets/product-card.liquid makes card links collection-aware server-side
  (/collections/x/products/y) so blocks/collection-nav.liquid can show back/next links on the
  product page. Cards that Horizon fetches after load (product recommendations, via
  /recommendations/products) render without a `collection` object in Liquid, so they fall
  back to the product's own first collection. On a collection-aware product page this
  rewrites those links to the current collection when the product is in it (the card carries
  its collection handles in data-collection-handles), so a shopper browsing AW24 stays in
  AW24. A product that is not in the current collection keeps its server-side fallback.

  On every page it also points the card's inner product links (the title link from
  blocks/product-title.liquid uses the plain product URL) at the same URL as the card's main
  link, so a click on the title carries the same collection context as a click on the image.
*/
(function () {
  const match = window.location.pathname.match(/^\/collections\/([^/]+)\/products\//);
  const current = match ? decodeURIComponent(match[1]) : null;

  const syncInnerLinks = (link) => {
    const card = link.closest("product-card");
    if (!card) return;
    const href = link.getAttribute("href");
    card.querySelectorAll(`a[href^="/products/${link.dataset.productHandle}"]`).forEach((inner) => {
      if (inner !== link) inner.setAttribute("href", href);
    });
  };

  const rewrite = (root) => {
    root.querySelectorAll("a.product-card__link[data-collection-handles]").forEach((link) => {
      const product = link.dataset.productHandle;
      if (!product) return;
      if (current && link.dataset.collectionContext !== current) {
        const handles = link.dataset.collectionHandles.split(",");
        if (handles.includes(current)) {
          const url = new URL(link.getAttribute("href"), window.location.origin);
          url.pathname = `/collections/${current}/products/${product}`;
          link.setAttribute("href", url.pathname + url.search + url.hash);
          link.dataset.collectionContext = current;
        }
      }
      syncInnerLinks(link);
    });

    // The recommendations carousel's pull zone (sections/product-recommendations.liquid) is
    // fetched without a collection context; point it at the same place as the sticky bar's
    // back link and name the collection when the collection-nav block knows its title.
    root.querySelectorAll("[data-kidsuper-collection-link]").forEach((el) => {
      const back = window.KidSuper.collectionBackLink?.();
      if (!back) return;
      const label = back.title ? `${el.dataset.labelIn || "View all in"} ${back.title}` : el.dataset.label || "View all";
      if (el instanceof HTMLAnchorElement) {
        el.setAttribute("href", back.href);
        el.textContent = label;
      } else {
        el.dataset.href = back.href;
        const text = el.querySelector(".recs-pull__label") || el;
        text.textContent = label;
      }
    });
  };

  // Only subtrees that actually contain a card are walked; drawer, modal and
  // header churn never reaches querySelectorAll. Observing #MainContent rather
  // than document.body keeps the callback off the drawers entirely.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches("product-card, [data-kidsuper-collection-link]") || el.querySelector("product-card, [data-kidsuper-collection-link]")) rewrite(el);
      }
    }
  });

  const boot = () => {
    rewrite(document);
    const root = document.getElementById("MainContent") || document.body;
    observer.observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.KidSuper.collectionContext = { rewrite };
})();

/*
  Keep the page still while a variant change swaps the media gallery.

  On every variant change Horizon replaces the whole <media-gallery> (assets/media-gallery.js,
  `this.replaceWith(newMediaGallery)`) instead of morphing it. The fresh slides use
  `content-visibility: auto` with no remembered size, so for a frame or two the gallery is
  0px tall and the document shrinks. Chrome's scroll anchoring absorbs that; Safari has none,
  so it clamps the scroll position to the shorter page and never restores it. Selecting a
  size from the sticky bar on iOS therefore threw the page up towards the gallery.

  Fix: on the picker's `shopify:product:select` event, pin the media column to its current
  height with min-height until the new gallery has laid out, then release. As a backstop, if
  the scroll position still moved during the swap without the shopper scrolling, put it back.
*/
(function () {
  const SETTLE_MS = 600;

  document.addEventListener("shopify:product:select", (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element) || target.closest("product-card, quick-add-dialog")) return;
    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== "function") return;

    const media = document.querySelector(".product-information__media");
    if (!(media instanceof HTMLElement)) return;

    // The scroll container is the window on mobile and .page-wrapper on the desktop squeeze layout.
    const pageWrapper = document.querySelector(".page-wrapper");
    const scroller =
      pageWrapper instanceof HTMLElement && pageWrapper.scrollHeight > pageWrapper.clientHeight ? pageWrapper : null;
    const readScroll = () => (scroller ? scroller.scrollTop : window.scrollY);
    const writeScroll = (y) => (scroller ? (scroller.scrollTop = y) : window.scrollTo(0, y));

    const height = media.getBoundingClientRect().height;
    if (height > 0) media.style.minHeight = `${height}px`;
    const startY = readScroll();
    let userScrolled = false;
    const onUserScroll = () => {
      userScrolled = true;
    };
    for (const type of ["touchstart", "wheel", "keydown"]) {
      window.addEventListener(type, onUserScroll, { passive: true, once: true });
    }

    const release = () => {
      window.setTimeout(() => {
        media.style.minHeight = "";
        requestAnimationFrame(() => {
          if (!userScrolled && Math.abs(readScroll() - startY) > 1) writeScroll(startY);
        });
      }, SETTLE_MS);
    };
    promise.then(release, release);
  });
})();

/*
  Recommendations pull zone (sections/product-recommendations.liquid, paged carousel).

  After the last page the scroller holds a `.recs-pull` zone that is not a snap point. The
  slideshow scroller is `scroll-snap-type: x mandatory`, so a normal swipe stops on the last
  page and a release inside the zone comes back to it: that snap is the friction. A drag that
  starts with the carousel resting on the last page and is still in progress when the pull
  passes THRESHOLD of the zone's width arms it; releasing then navigates to the collection
  (destination and label filled in by the collection-context rewrite above). A fling from an
  earlier page cannot arm it because the gesture did not start at rest on the last page.
  Desktop has no drag, so a click on the zone navigates.

  The return from a released, un-armed pull is animated here rather than left to the
  browser: the native mandatory-snap return is brisk and mechanical next to the elastic
  left-edge bounce (Alan, 2026-09-01: "come back softer and not very fast"). Snapping is
  disabled for the duration so the UA cannot race the animation, and restored once the
  scroller is exactly on the last page's snap offset, so nothing jumps. Deliberately not
  gated on prefers-reduced-motion, same reasoning as the sticky bar's transition in
  kidsuper-custom.css: Alan's phone has Reduce Motion on, and gating would hand the harsh
  native return right back.

  Ease-out over 600ms is Alan's pick from two on-page A/B rounds on his iPhone
  (2026-09-01, a ?pulltune=1 overlay since removed): it beat critically damped springs
  (700/950ms) and a variant that waited out the release momentum before animating. If the
  feel is ever revisited, resurrect the tuner from git history rather than deploying one
  candidate at a time; the preview cache lag makes per-deploy testing ambiguous.
*/
(function () {
  const THRESHOLD = 0.6;
  const WHEEL_IDLE_MS = 160;
  const RESTORE_COOLDOWN_MS = 800;
  const RETURN_MS = 600;
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  /**
   * @param {HTMLElement} zone
   */
  function setup(zone) {
    if (zone.dataset.pullInit) return;
    zone.dataset.pullInit = "true";
    const scroller = zone.closest("slideshow-slides");
    if (!(scroller instanceof HTMLElement)) return;

    const slideshow = /** @type {any} */ (zone.closest("slideshow-component"));
    // Direct children only: the cards inside each page are slideshow-slides too.
    const lastIndex = () => scroller.querySelectorAll(":scope > slideshow-slide").length - 1;

    // The pull is measured from the scroll position recorded when the drag started, so no
    // snap geometry (gutters, scroll-padding) has to be reproduced here.
    let restLeft = null;
    let tracking = false;
    let lastScrollAt = 0;

    const progress = () => {
      if (restLeft === null || zone.offsetWidth === 0) return 0;
      return Math.max(0, Math.min(1, (scroller.scrollLeft - restLeft) / (zone.offsetWidth * THRESHOLD)));
    };
    const render = () => {
      const p = progress();
      zone.style.setProperty("--pull", p.toFixed(3));
      zone.dataset.armed = p >= 1 ? "true" : "false";
      return p;
    };
    const go = () => {
      const href = zone.dataset.href;
      if (href) window.location.assign(href);
    };

    // Soft return machinery. While a return is animating (or was interrupted mid-zone),
    // snapping stays off and restLeft keeps pointing at the last page's snap offset.
    let returnFrame = 0;
    let snapOff = false;

    const setSnapOff = (off) => {
      if (snapOff === off) return;
      snapOff = off;
      scroller.style.scrollSnapType = off ? "none" : "";
      scroller.style.scrollBehavior = off ? "auto" : "";
    };
    const stopReturn = () => {
      if (returnFrame) {
        window.cancelAnimationFrame(returnFrame);
        returnFrame = 0;
      }
    };
    const settle = () => {
      stopReturn();
      setSnapOff(false);
      restLeft = null;
      render();
    };
    const softReturn = () => {
      const from = scroller.scrollLeft;
      const to = restLeft;
      // At (or left of) the rest position there is nothing to animate; hand straight
      // back to native snapping.
      if (to === null || from - to < 1) {
        settle();
        return;
      }
      setSnapOff(true);
      const startAt = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startAt) / RETURN_MS);
        if (t < 1) {
          scroller.scrollLeft = from + (to - from) * easeOutQuart(t);
          returnFrame = window.requestAnimationFrame(step);
        } else {
          scroller.scrollLeft = to;
          returnFrame = 0;
          settle();
        }
      };
      returnFrame = window.requestAnimationFrame(step);
    };

    const begin = () => {
      stopReturn();
      if (snapOff) {
        // The finger caught the carousel mid-return. Keep the origin and the disabled
        // snapping; this touch's release lands in end(), which resumes the return from
        // wherever the finger leaves it. No arming from a caught return: restLeft must
        // stay the last page's snap offset for the resume to aim right.
        tracking = false;
        render();
        return;
      }
      const onLastPage = slideshow?.current === lastIndex();
      const atRest = performance.now() - lastScrollAt > 80;
      tracking = onLastPage && atRest;
      restLeft = tracking ? scroller.scrollLeft : null;
      render();
    };
    const end = () => {
      const armed = tracking && progress() >= 1;
      tracking = false;
      if (armed) {
        go();
        return;
      }
      softReturn();
    };

    // Touch: the drag is bounded by touchstart / touchend.
    scroller.addEventListener("touchstart", begin, { passive: true });
    scroller.addEventListener("touchend", end, { passive: true });
    scroller.addEventListener("touchcancel", end, { passive: true });

    // Trackpad (desktop Safari, Chrome): a horizontal wheel stream has no start or end
    // event, so the first wheel after rest begins a drag and WHEEL_IDLE_MS of silence ends it.
    let wheelTimer = 0;
    scroller.addEventListener(
      "wheel",
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (!tracking) begin();
        if (!tracking) return;
        window.clearTimeout(wheelTimer);
        wheelTimer = window.setTimeout(end, WHEEL_IDLE_MS);
      },
      { passive: true }
    );

    scroller.addEventListener(
      "scroll",
      () => {
        lastScrollAt = performance.now();
        render();
      },
      { passive: true }
    );
    zone.addEventListener("click", go);

    // Back/forward cache: Safari restores this page with its JS state intact and the
    // scroller still inside the zone, while trackpad inertia keeps sending wheel events.
    // Drop any in-flight gesture on the way out, and on the way back ignore the first
    // moments and put the carousel back on the last page.
    const reset = () => {
      window.clearTimeout(wheelTimer);
      tracking = false;
      settle();
    };
    window.addEventListener("pagehide", reset);
    window.addEventListener("pageshow", () => {
      reset();
      lastScrollAt = performance.now() + RESTORE_COOLDOWN_MS;
      if (slideshow?.current === lastIndex() && typeof slideshow.select === "function") {
        slideshow.select(lastIndex());
      }
    });
    render();
  }

  const boot = (root = document) => {
    root.querySelectorAll(".recs-pull").forEach((el) => setup(/** @type {HTMLElement} */ (el)));
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches(".recs-pull") || el.querySelector(".recs-pull")) boot(el);
      }
    }
  });

  const start = () => {
    boot();
    const main = document.getElementById("MainContent") || document.body;
    observer.observe(main, { childList: true, subtree: true });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  document.addEventListener("shopify:section:load", () => boot());

  window.KidSuper.recsPull = { boot };
})();
